<template>
  <div>
    <HrPageLayout
      title="Employee Certifications"
      subtitle="View and manage employee certifications"
      icon="📜"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Training & Development' },
        { label: 'Employee Certifications' }
      ]"
    >
      <template #header-actions>
        <button type="button" class="hr-btn hr-btn-primary" @click="openAdd">
          <i class="bx bx-plus"></i> Add Certification
        </button>
      </template>

      <div class="certifications-content">
        <div v-if="message" class="hr-alert" :class="messageSuccess ? 'hr-alert-success' : 'hr-alert-danger'">
          {{ message }}
          <button type="button" class="hr-btn hr-btn-sm" @click="message = ''">&times;</button>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="hr-filter-group">
              <label class="hr-form-label">Employee (emp no)</label>
              <input v-model="filterEmpNo" class="hr-input" placeholder="Leave empty for all" style="width: 180px;" @keyup.enter="loadCertifications">
            </div>
            <button type="button" class="hr-btn hr-btn-primary" @click="loadCertifications">
              <i class="bx bx-search"></i> Load
            </button>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted">Loading certifications...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Certificate</th>
                    <th>Issuing body</th>
                    <th>Issue date</th>
                    <th>Expiry</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, idx) in certifications" :key="c.certificateID">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ c.empNo }}</td>
                    <td>{{ c.certificateName }} <span v-if="c.certificateNumber" class="hr-text-muted">({{ c.certificateNumber }})</span></td>
                    <td>{{ c.issuingBody || '—' }}</td>
                    <td>{{ c.issueDate }}</td>
                    <td>{{ c.expiryDate || '—' }}</td>
                    <td><span class="hr-badge" :class="statusClass(c.verificationStatus)">{{ c.verificationStatus }}</span></td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openEdit(c)"><i class="bx bx-edit"></i></button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="confirmDelete(c)"><i class="bx bx-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="certifications.length === 0">
                    <td colspan="8" class="hr-text-center hr-py-4 hr-text-muted">No certifications found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Add/Edit Modal -->
    <div class="hr-modal-overlay" v-if="showModal" @click.self="showModal = false">
      <div class="hr-modal hr-modal-medium">
        <div class="hr-modal-header">
          <h3>{{ editingId ? 'Edit Certification' : 'Add Certification' }}</h3>
          <button type="button" class="hr-modal-close" @click="showModal = false">&times;</button>
        </div>
        <form @submit.prevent="saveCert" class="hr-form-grid hr-p-3">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Employee No <span class="hr-text-danger">*</span></label>
            <input v-model="form.empNo" class="hr-input" required placeholder="empNo">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Training (optional)</label>
            <select v-model="form.trainingID" class="hr-select">
              <option value="">— None —</option>
              <option v-for="t in trainings" :key="t.trainingID" :value="t.trainingID">{{ t.trainingName }}</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Certificate number</label>
            <input v-model="form.certificateNumber" class="hr-input" placeholder="Ref number">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Certificate name <span class="hr-text-danger">*</span></label>
            <input v-model="form.certificateName" class="hr-input" required placeholder="Name">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Issuing body</label>
            <input v-model="form.issuingBody" class="hr-input" placeholder="Issuing body">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Issue date <span class="hr-text-danger">*</span></label>
            <input v-model="form.issueDate" class="hr-input" type="date" required>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Expiry date</label>
            <input v-model="form.expiryDate" class="hr-input" type="date">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Verification status</label>
            <select v-model="form.verificationStatus" class="hr-select">
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div class="hr-modal-footer span-full">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showModal = false">Cancel</button>
            <button type="submit" class="hr-btn hr-btn-primary" :disabled="saving">Save</button>
          </div>
        </form>
      </div>
    </div>

    <div class="hr-modal-overlay" v-if="deleteTarget" @click.self="deleteTarget = null">
      <div class="hr-modal hr-modal-small">
        <div class="hr-modal-header"><h3>Delete certification</h3><button type="button" class="hr-modal-close" @click="deleteTarget = null">&times;</button></div>
        <div class="hr-p-3">
          <p>Delete "{{ deleteTarget && deleteTarget.certificateName }}"?</p>
          <div class="hr-mt-3 hr-flex gap-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="deleteTarget = null">Cancel</button>
            <button type="button" class="hr-btn hr-btn-danger" @click="doDelete" :disabled="deleting">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeCertifications",
  components: { HrPageLayout },
  data() {
    return {
      certifications: [],
      trainings: [],
      loading: false,
      message: '',
      messageSuccess: false,
      filterEmpNo: '',
      showModal: false,
      editingId: 0,
      saving: false,
      form: {
        empNo: '',
        trainingID: '',
        certificateNumber: '',
        certificateName: '',
        issuingBody: '',
        issueDate: '',
        expiryDate: '',
        verificationStatus: 'pending'
      },
      deleteTarget: null,
      deleting: false
    };
  },
  mounted() {
    this.loadTrainings();
    this.loadCertifications();
  },
  methods: {
    statusClass(s) {
      const m = { verified: 'hr-badge-success', pending: 'hr-badge-warning', rejected: 'hr-badge-danger', expired: 'hr-badge-secondary' };
      return m[s] || 'hr-badge-info';
    },
    async loadTrainings() {
      const data = new FormData();
      data.append("function", "getTrainings");
      data.append("activeOnly", "0");
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) this.trainings = res.data.data.trainings || [];
      } catch (_) {
        // Optional: trainings list empty if load fails
      }
    },
    async loadCertifications() {
      this.loading = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "getEmployeeCertifications");
      if (this.filterEmpNo.trim()) data.append("empNo", this.filterEmpNo.trim());
      else data.append("all", "1");
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) this.certifications = res.data.data.certifications || [];
      } catch (e) {
        this.message = e.message || 'Failed to load';
        this.messageSuccess = false;
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editingId = 0;
      this.form = { empNo: this.filterEmpNo || '', trainingID: '', certificateNumber: '', certificateName: '', issuingBody: '', issueDate: '', expiryDate: '', verificationStatus: 'pending' };
      this.showModal = true;
    },
    openEdit(c) {
      this.editingId = c.certificateID;
      this.form = {
        empNo: c.empNo,
        trainingID: c.trainingID || '',
        certificateNumber: c.certificateNumber || '',
        certificateName: c.certificateName || '',
        issuingBody: c.issuingBody || '',
        issueDate: c.issueDate || '',
        expiryDate: c.expiryDate || '',
        verificationStatus: c.verificationStatus || 'pending'
      };
      this.showModal = true;
    },
    async saveCert() {
      this.saving = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "saveEmployeeCertification");
      data.append("certificateID", this.editingId);
      data.append("empNo", this.form.empNo);
      data.append("trainingID", this.form.trainingID || '');
      data.append("certificateNumber", this.form.certificateNumber);
      data.append("certificateName", this.form.certificateName);
      data.append("issuingBody", this.form.issuingBody);
      data.append("issueDate", this.form.issueDate);
      data.append("expiryDate", this.form.expiryDate);
      data.append("verificationStatus", this.form.verificationStatus);
      data.append("createdBy", sessionStorage.getItem("sessionNames") || '');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = res.data.message || 'Saved';
          this.messageSuccess = true;
          this.showModal = false;
          this.loadCertifications();
        } else {
          this.message = res.data.message || 'Save failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Save failed';
        this.messageSuccess = false;
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(c) { this.deleteTarget = c; },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deleteEmployeeCertification");
      data.append("certificateID", this.deleteTarget.certificateID);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = 'Deleted';
          this.messageSuccess = true;
          this.deleteTarget = null;
          this.loadCertifications();
        } else {
          this.message = res.data.message || 'Delete failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Delete failed';
        this.messageSuccess = false;
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.certifications-content { padding: 0; }
.hr-alert { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; }
.hr-alert-success { background: #d1fae5; color: #065f46; }
.hr-alert-danger { background: #fee2e2; color: #991b1b; }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.hr-modal { background: #fff; border-radius: 16px; max-width: 96vw; max-height: 90vh; overflow-y: auto; }
.hr-modal-medium { width: 560px; }
.hr-modal-small { width: 400px; }
.hr-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #64748b; }
.hr-modal-footer { display: flex; gap: 12px; justify-content: flex-end; margin-top: 16px; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.hr-p-3 { padding: 20px; }
.hr-mt-3 { margin-top: 16px; }
.hr-flex.gap-2 { display: flex; gap: 8px; }
</style>
